import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { useDnsRecordMutations } from "@/features/dns/hooks/useDns";

const simpleFormSchema = z.object({
  subdomain: z.string().optional(),
  ip: z.string().min(1, "La IP es obligatoria"),
});

const fullFormSchema = z.object({
  type: z.string().min(1, "El tipo es obligatorio"),
  name: z.string().min(1, "El nombre es obligatorio"),
  content: z.string().min(1, "El contenido es obligatorio"),
  ttl: z.coerce.number().int().min(60),
  proxied: z.boolean().optional(),
  priorityRaw: z.string().optional(),
  comment: z.string().optional(),
});

type SimpleForm = z.infer<typeof simpleFormSchema>;
type FullFormInput = z.input<typeof fullFormSchema>;
type FullFormValues = z.output<typeof fullFormSchema>;

interface DnsCreateRecordDialogProps {
  mutations: ReturnType<typeof useDnsRecordMutations>;
}

export function DnsCreateRecordDialog({ mutations }: DnsCreateRecordDialogProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"simple" | "full">("simple");

  const simpleForm = useForm<SimpleForm>({
    resolver: zodResolver(simpleFormSchema),
    defaultValues: { subdomain: "", ip: "" },
  });

  const fullForm = useForm<FullFormInput, unknown, FullFormValues>({
    resolver: zodResolver(fullFormSchema),
    defaultValues: {
      type: "A",
      name: "",
      content: "",
      ttl: 120,
      proxied: false,
      priorityRaw: "",
    },
  });

  const resetAll = () => {
    simpleForm.reset({ subdomain: "", ip: "" });
    fullForm.reset({
      type: "A",
      name: "",
      content: "",
      ttl: 120,
      proxied: false,
      priorityRaw: "",
    });
    setMode("simple");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) resetAll();
  };

  const onSubmitSimple = (values: SimpleForm) => {
    const body: Record<string, unknown> = {
      subdomain: values.subdomain?.trim() || "@",
      ip: values.ip.trim(),
    };
    mutations.createMut.mutate(body, {
      onSuccess: () => {
        handleOpenChange(false);
      },
    });
  };

  const onSubmitFull = (values: FullFormValues) => {
    const pr = values.priorityRaw?.trim();
    const priorityParsed = pr ? parseInt(pr, 10) : NaN;
    const body: Record<string, unknown> = {
      type: values.type,
      name: values.name,
      content: values.content,
      ttl: values.ttl,
      ...(values.proxied !== undefined ? { proxied: values.proxied } : {}),
      ...(Number.isFinite(priorityParsed) ? { priority: priorityParsed } : {}),
      ...(values.comment ? { comment: values.comment } : {}),
    };
    mutations.createMut.mutate(body, {
      onSuccess: () => {
        handleOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Nuevo registro</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Crear registro DNS</DialogTitle>
          <DialogDescription>
            Modo simple para registros A (subdominio + IP) o modo completo al estilo Cloudflare.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 py-2">
          <Button
            type="button"
            variant={mode === "simple" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("simple")}
          >
            Simple
          </Button>
          <Button
            type="button"
            variant={mode === "full" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("full")}
          >
            Completo
          </Button>
        </div>

        {mode === "simple" ? (
          <form onSubmit={simpleForm.handleSubmit(onSubmitSimple)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dns-sub">Subdominio</Label>
              <Input
                id="dns-sub"
                placeholder="mi-servicio (vacío o @ = apex)"
                {...simpleForm.register("subdomain")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dns-ip">IP</Label>
              <Input id="dns-ip" placeholder="203.0.113.10" {...simpleForm.register("ip")} />
              {simpleForm.formState.errors.ip && (
                <p className="text-sm text-destructive">{simpleForm.formState.errors.ip.message}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutations.createMut.isPending}>
                {mutations.createMut.isPending ? "Creando…" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={fullForm.handleSubmit(onSubmitFull)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Input {...fullForm.register("type")} />
              </div>
              <div className="space-y-2">
                <Label>TTL</Label>
                <Input type="number" {...fullForm.register("ttl")} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre (FQDN)</Label>
              <Input {...fullForm.register("name")} />
            </div>
            <div className="space-y-2">
              <Label>Contenido</Label>
              <Input {...fullForm.register("content")} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="dns-px" {...fullForm.register("proxied")} />
              <Label htmlFor="dns-px">Proxied</Label>
            </div>
            <div className="space-y-2">
              <Label>Prioridad (opcional)</Label>
              <Input type="number" {...fullForm.register("priorityRaw")} />
            </div>
            <div className="space-y-2">
              <Label>Comentario (opcional)</Label>
              <Input {...fullForm.register("comment")} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutations.createMut.isPending}>
                {mutations.createMut.isPending ? "Creando…" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
